// EINE native Audio-Sitzung fuer die ganze App — mit Besitzern statt globalem
// Start/Stop (gleiches Muster wie ./screenWakeLock.ts).
//
// WARUM: startNativeCallMode/stopNativeCallMode (../native/liveCall) sprechen
// eine EINZIGE native Ressource an (Android Foreground-Service + Notification,
// iOS AVAudioSession). Vier Stellen wollen sie inzwischen: das Live-Gespraech,
// die Tischrunden-Aufnahme, das Hoerbuch und — seit die Klangbuehne app-weit
// weiterlaeuft — der SoundboardPlayerProvider. Ohne Zaehler riss der Stopp des
// einen die Hintergrund-Wiedergabe des anderen ab (Julius' „hartnaeckige"
// Klangbuehne starb, sobald man das Hoerbuch beendete).
//
// REGELN:
//  - stop() erst, wenn der LETZTE Besitzer geht.
//  - Die Notification gehoert dem Besitzer mit dem hoechsten Rang; bei
//    Gleichstand dem zuletzt hinzugekommenen. Geht er, uebernimmt der naechste
//    (start() wird mit dessen Texten erneut gerufen — nativ ein Update).
//  - Alle Bruecken-Aufrufe laufen SERIALISIERT durch eine Promise-Kette: ein
//    spaeter angestossenes stop() kann ein frueheres start() nicht mehr
//    ueberholen (die Rennbedingung, die eine laufende, bezahlte Live-Sitzung
//    ihren Foreground-Service kosten konnte).
//
// Alles ist best effort: ohne Plugin (Web/PWA/Alt-Shell) sind start/stop
// No-Ops, und Fehler werden in liveCall.ts bereits geschluckt.
import {
  startNativeCallMode,
  stopNativeCallMode,
  type LiveCallStartOptions,
} from '../native/liveCall';

/**
 * Rangfolge der Besitzer. Wer telefoniert oder aufnimmt, bestimmt die
 * Notification — die Klangbuehne ist nur Untermalung und gibt sie ab.
 */
export const NATIVE_AUDIO_RANK = {
  soundboard: 10,
  audiobook: 20,
  call: 30,
} as const;

export type NativeAudioRank = (typeof NATIVE_AUDIO_RANK)[keyof typeof NATIVE_AUDIO_RANK];

interface OwnerEntry {
  opts: LiveCallStartOptions;
  rank: number;
  seq: number;
}

const owners = new Map<string, OwnerEntry>();
let seqCounter = 0;
// Token des Besitzers, dessen Texte gerade nativ stehen (`null` = keine Sitzung).
let currentToken: string | null = null;
// Serialisierung aller Bruecken-Aufrufe.
let chain: Promise<void> = Promise.resolve();

function enqueue(task: () => Promise<void>): void {
  chain = chain.then(task, task).catch(() => undefined);
}

function pickWinner(): { owner: string; entry: OwnerEntry } | null {
  let best: { owner: string; entry: OwnerEntry } | null = null;
  for (const [owner, entry] of owners) {
    if (
      best === null
      || entry.rank > best.entry.rank
      || (entry.rank === best.entry.rank && entry.seq > best.entry.seq)
    ) {
      best = { owner, entry };
    }
  }
  return best;
}

function sync(): void {
  const next = pickWinner();
  if (!next) {
    if (currentToken === null) return;
    currentToken = null;
    enqueue(() => stopNativeCallMode());
    return;
  }
  const token = `${next.owner}:${next.entry.seq}`;
  if (token === currentToken) return;
  currentToken = token;
  const { opts } = next.entry;
  enqueue(() => startNativeCallMode(opts));
}

/**
 * Nativen Audio-Modus fuer diesen Besitzer anfordern. Mehrfaches Anfordern
 * desselben Besitzers aktualisiert nur seine Texte (und damit ggf. die
 * Notification, wenn er sie gerade haelt).
 */
export function acquireNativeAudioSession(
  owner: string,
  opts: LiveCallStartOptions,
  rank: number = NATIVE_AUDIO_RANK.call,
): void {
  seqCounter += 1;
  owners.set(owner, { opts, rank, seq: seqCounter });
  sync();
}

/**
 * Besitzer abmelden. Die native Sitzung wird NUR abgebaut, wenn er der letzte
 * war; sonst uebernimmt der naechste Besitzer die Notification.
 */
export function releaseNativeAudioSession(owner: string): void {
  if (!owners.delete(owner)) return;
  sync();
}

/** Nur fuer Tests/geordnete Teardowns: wartet die Bruecken-Kette ab. */
export function flushNativeAudioSession(): Promise<void> {
  return chain;
}

/** Nur fuer Tests: aktueller Notification-Besitzer (null = keine Sitzung). */
export function currentNativeAudioOwner(): string | null {
  return currentToken === null ? null : currentToken.slice(0, currentToken.lastIndexOf(':'));
}
